import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "./AuthContext";

export const PositionContext = createContext();

export const PositionProvider = ({ children }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);

  const API_BASE_URL = process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";

  /**
   * Fetch all positions for the current user
   */
  const fetchUserPositions = async (userId, status = null) => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const url = status
        ? `${API_BASE_URL}/api/positions/user/${userId}?status=${status}`
        : `${API_BASE_URL}/api/positions/user/${userId}`;

      const response = await axios.get(url);
      setPositions(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching positions:", err);
      setError(err.response?.data?.message || "Failed to fetch positions");
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create a new position
   */
  const addPosition = async (positionData) => {
    if (!user?.token) {
      setError("You must be logged in to create a position");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/positions/create`,
        {
          type: positionData.type,
          ticker: positionData.ticker,
          entryPrice: parseFloat(positionData.entryPrice),
          quantity: parseFloat(positionData.quantity),
          entryDate: positionData.entryDate
            ? `${positionData.entryDate}T00:00:00`
            : new Date().toISOString(),
          stopLoss: positionData.stopLoss ? parseFloat(positionData.stopLoss) : null,
          takeProfit: positionData.takeProfit ? parseFloat(positionData.takeProfit) : null,
          strikePrice: positionData.strikePrice ? parseFloat(positionData.strikePrice) : null,
          expirationDate: positionData.expirationDate || null,
          optionType: positionData.optionType || null,
          premium: positionData.premium ? parseFloat(positionData.premium) : null,
          lowerPutStrike: positionData.lowerPutStrike ? parseFloat(positionData.lowerPutStrike) : null,
          upperPutStrike: positionData.upperPutStrike ? parseFloat(positionData.upperPutStrike) : null,
          lowerCallStrike: positionData.lowerCallStrike ? parseFloat(positionData.lowerCallStrike) : null,
          upperCallStrike: positionData.upperCallStrike ? parseFloat(positionData.upperCallStrike) : null,
          notes: positionData.notes || null,
          proofImageUrl: positionData.proofImageUrl || null,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const newPosition = response.data;
      setPositions((prev) => [...prev, newPosition]);
      return newPosition;
    } catch (err) {
      console.error("Error creating position:", err);
      setError(err.response?.data?.message || "Failed to create position");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an existing position (close/partial close)
   */
  const updatePosition = async (positionId, updateData) => {
    if (!user?.token) {
      setError("You must be logged in to update a position");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/positions/${positionId}`,
        {
          status: updateData.status || null,
          exitPrice: updateData.exitPrice ? parseFloat(updateData.exitPrice) : null,
          exitDate: updateData.exitDate || new Date().toISOString(),
          notes: updateData.notes || null,
          quantityClosed: updateData.quantityClosed ? parseFloat(updateData.quantityClosed) : null,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const updatedPosition = response.data;
      setPositions((prev) =>
        prev.map((pos) =>
          pos.positionId === positionId ? updatedPosition : pos
        )
      );
      return updatedPosition;
    } catch (err) {
      console.error("Error updating position:", err);
      setError(err.response?.data?.message || "Failed to update position");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a position
   */
  const removePosition = async (positionId) => {
    if (!user?.token) {
      setError("You must be logged in to delete a position");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(`${API_BASE_URL}/api/positions/${positionId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      setPositions((prev) => prev.filter((pos) => pos.positionId !== positionId));
      return true;
    } catch (err) {
      console.error("Error deleting position:", err);
      setError(err.response?.data?.message || "Failed to delete position");
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get a single position by ID
   */
  const getPositionById = async (positionId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/positions/${positionId}`
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching position:", err);
      setError(err.response?.data?.message || "Failed to fetch position");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Upload proof image for a position
   */
  const uploadProofImage = async (positionId, file) => {
    if (!user?.token) {
      setError("You must be logged in to upload proof");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API_BASE_URL}/api/positions/${positionId}/upload-proof`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.data; // Returns the image URL
    } catch (err) {
      console.error("Error uploading proof image:", err);
      setError(err.response?.data?.message || "Failed to upload proof image");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load user's positions on component mount
   */
  useEffect(() => {
    if (user?.id) {
      fetchUserPositions(user.id);
    }
  }, [user?.id]);

  return (
    <PositionContext.Provider
      value={{
        positions,
        loading,
        error,
        addPosition,
        removePosition,
        updatePosition,
        fetchUserPositions,
        getPositionById,
        uploadProofImage,
        setError,
      }}
    >
      {children}
    </PositionContext.Provider>
  );
};
